import { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import api from '../../../api'
import { CampaignContext, ProjectContext } from '../../../contexts'
import { ChannelType, Template } from '../../../types'
import Button from '../../../ui/Button'
import { Column } from '../../../ui/Columns'
import Heading from '../../../ui/Heading'
import Preview from '../../../ui/Preview'
import Alert from '../../../ui/Alert'
import SourceEditor from '../../../ui/SourceEditor'
import { UserLookup } from '../../users/UserLookup'
import CampaignSendProof from '../preview/CampaignSendProof'
import { flattenUser } from '../../../ui/utils'
import { debounce } from '../../../utils'
import { toast } from 'react-hot-toast/headless'
import '../CampaignPreview.css'
import { Menu, MenuItem } from '../../../ui'
import { CloseIcon, EyeIcon, SendIcon, UploadIcon } from '../../../ui/icons'

interface TemplatePreviewPanelProps {
    templateId: number
    type: ChannelType
    data: Template['data']
    prerenderData?: Template['data']
    locale: string
    showCodeEditor: boolean
}

export default function TemplatePreviewPanel({ templateId, type, data, prerenderData, locale }: TemplatePreviewPanelProps) {
    const { t } = useTranslation()
    const [project] = useContext(ProjectContext)
    const [campaign] = useContext(CampaignContext)

    const [isPreviewOpen, setIsPreviewOpen] = useState(false)
    const [liveData, setLiveData] = useState(data)
    const [templatePreviewError, setTemplatePreviewError] = useState<string | undefined>()
    const [editorValue, setEditorValue] = useState<string>(
        campaign.type === 'trigger'
            ? '{\n    "user": {},\n    "journey": {}\n}'
            : '{\n    "user": {}\n}',
    )
    const [isSendProofOpen, setIsSendProofOpen] = useState(false)
    const [proofResponse, setProofResponse] = useState<any>(undefined)
    const [isUserLookupOpen, setIsUserLookupOpen] = useState(false)
    const hasJourneys = !!campaign.journeys?.length

    const handleEditorChange = useMemo(() => debounce(async (value?: string) => {
        try {
            const { data } = await api.templates.preview(project.id, templateId, parseOrDefault(value))
            setTemplatePreviewError(undefined)
            setLiveData(data)
        } catch (error: any) {
            if (error?.response?.data.error) {
                setTemplatePreviewError(error.response.data.error)
                return
            }
            setTemplatePreviewError(error.message)
        }
    }), [templateId])

    useEffect(() => {
        if (isPreviewOpen) {
            handleEditorChange(editorValue)
        }
    }, [editorValue, isPreviewOpen, templateId, data])

    const handleDataChange = (newValue: any) => {
        setEditorValue(
            JSON.stringify({
                ...parseOrDefault(),
                ...newValue,
            }, undefined, 4),
        )
    }

    const parseOrDefault = (value = editorValue, defaultValue = '{}') => {
        try {
            return JSON.parse(value ?? defaultValue)
        } catch {
            return {}
        }
    }

    const handleJourneySample = async () => {
        if (campaign.journeys?.length) {
            const journey = campaign.journeys[0]
            const sample = await api.journeys.steps.sample(project.id, journey.id)
            handleDataChange({ journey: sample })
        } else {
            toast.error(t('template_journey_sample_error'))
        }
    }

    return (
        <>
            <Column fullscreen={true}>
                <Heading title={isPreviewOpen ? t('preview') : t('preview')} size="h4" actions={
                    <>
                        <Button
                            size="small"
                            variant="secondary"
                            icon={<SendIcon />}
                            onClick={() => setIsSendProofOpen(true)}
                        >
                            {type === 'webhook' ? t('test_webhook') : t('send_proof')}
                        </Button>
                        {!isPreviewOpen && (
                            <Button
                                size="small"
                                variant="secondary"
                                icon={<EyeIcon />}
                                onClick={() => setIsPreviewOpen(true)}
                            >
                                {t('data_preview')}
                            </Button>
                        )}
                    </>
                } />
                {isPreviewOpen && templatePreviewError && (
                    <Alert variant="warn" title={t('template_error')}>
                        {t('template_handlebars_error')}{templatePreviewError}
                    </Alert>
                )}
                <Preview
                    template={{
                        type,
                        data: isPreviewOpen ? liveData : (prerenderData ?? data),
                        locale,
                    }}
                    response={proofResponse}
                />
            </Column>

            {isPreviewOpen && (
                <Column fullscreen={true}>
                    <Heading title={t('data')} size="h4" actions={
                        <>
                            {hasJourneys
                                ? (
                                    <Menu button={
                                        <Button
                                            size="small"
                                            variant="secondary"
                                            icon={<UploadIcon />}
                                        >{t('data_load')}</Button>
                                    }>
                                        <MenuItem onClick={() => setIsUserLookupOpen(true)}>{t('load_user')}</MenuItem>
                                        <MenuItem onClick={async () => await handleJourneySample()}>{t('template_journey_sample')}</MenuItem>
                                    </Menu>
                                )
                                : (
                                    <Button
                                        size="small"
                                        variant="secondary"
                                        onClick={() => setIsUserLookupOpen(true)}
                                    >{t('load_user')}</Button>
                                )}
                            <Button
                                size="small"
                                variant="secondary"
                                icon={<CloseIcon />}
                                onClick={() => setIsPreviewOpen(false)}
                            >{t('data_close')}</Button>
                        </>
                    } />
                    <div className="preview-source-editor">
                        <SourceEditor
                            defaultLanguage="json"
                            value={editorValue}
                            onChange={(value) => {
                                if (value) setEditorValue(value)
                            }}
                            showMinimap={false}
                        />
                    </div>
                </Column>
            )}

            <UserLookup
                open={isUserLookupOpen}
                onClose={setIsUserLookupOpen}
                onSelected={user => handleDataChange({ user: flattenUser(user) })}
            />
            <CampaignSendProof
                open={isSendProofOpen}
                onClose={setIsSendProofOpen}
                templateId={templateId}
                type={type}
                variables={parseOrDefault()}
                onResponse={setProofResponse}
            />
        </>
    )
}
